import React from 'react';
import { useIntl } from 'react-intl';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { HubCrossLinks, type HubCrossLink } from '../../../../../components/hub/HubCrossLinks';
import { RoleFollowButton } from '../../../../../components/roles/RoleFollowButton';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { resolveSearchHitHostedLogoUrl } from '../../../../../utils/search/resolve-search-hit-company-display';
import { buildJobMapCommuteHref } from '../../../../../utils/map/job-map-commute-href';
import type { Locale } from '../../../../../utils/routing/generate-url';
import { OverviewSectionHeader, ApplyButton } from '../components';
import { SimilarJobsSection } from '../components/SimilarJobsSection';
import { BrandRail } from './BrandRail';
import { MetaGrid } from './MetaGrid';
import { EditorialHeadlineFacts } from './editorial-rail/EditorialHeadlineFacts';
import { EditorialDescription } from './editorial-rail/EditorialDescription';
import { EditorialFitLedger } from './editorial-rail/EditorialFitLedger';
import { EditorialDossier } from './editorial-rail/EditorialDossier';
import { JobLocationMapModal } from './editorial-rail/JobLocationMapModal';
import { useHomeCommute } from '../../../../../hooks/useHomeCommute';
import { primaryCommute } from '../../../../../utils/map/commute-mode-order';
import { formatCommuteMinutes } from './editorial-rail/commute-copy';
import { isSeaFallbackGeoloc } from '../../../../../utils/map/sea-fallback-geoloc';
import { CollectiveAgreementNotice } from './CollectiveAgreementNotice';
import type { OverviewSectionHeaderProps } from '../components/OverviewSectionHeader';
import type { DisplayJob } from '../../../../../types';
import type { FinnishNetSalaryEstimate } from '../utils/estimateFinnishNetSalary';
import { useFormatMessage } from '../../../../../i18n-review/client/Msg';

export interface JobDetailPageV2EditorialRailProps {
  displayJob: DisplayJob;
  title?: string;
  normalisedTitle?: string;
  companyId?: string | null;
  location?: string;
  remoteness?: string;
  /** TEC-482 follow-up: see JobDetailPageV2Props.remoteScopes. */
  remoteScopes?: import('../../../../../utils/location/structured-job-location-display').StructuredRemoteScope[];
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
  collectiveAgreementId?: string | null;
  collectiveAgreementName?: string | null;
  collectiveAgreementMatchType?: 'explicit' | 'inferred' | 'none' | null;
  collectiveAgreementReasoning?: string | null;
  collectiveAgreementConfidence?: number | null;
  jobLocale: string;
  displayDescription?: string;
  overviewHeaderProps: OverviewSectionHeaderProps;
  applyUrl: string;
  isApplied: boolean;
  setAppliedJobState: (job: any, applied: boolean) => Promise<void>;
  onTailorCv?: (opts?: { source?: 'job_page' | 'saved_jobs' | 'deep_link' | 'match_score'; tab?: 'cv' | 'letter' }) => void;
  formattedPostedDate: string | null;
  postedDaysAgo: number | null;
  formattedLastSeenDate: string | null;
  formattedApplicationDate: string | null;
  formattedEstimatedExpiryDate: string | null;
  matchScoreData: import('../../../../../utils/data/calculate-job-match-score').MatchScoreBreakdown | null;
  matchBreakdownExpanded: boolean;
  onToggleMatchBreakdownExpanded: () => void;
  salaryRangeMin?: number;
  salaryRangeMax?: number;
  locationForChart?: string[];
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  salaryListingProvenance?: import('../../../../../utils/job-detail/resolve-job-salary-listing-provenance').JobSalaryListingProvenance;
  optionalLanguages?: string[];
  perks?: string[];
  jobDetailsRef: React.RefObject<HTMLDivElement>;
  currentLocaleTrends?: {
    roleDistribution: Record<string, number>;
    roleLevelDistribution?: Record<string, number>;
    locationDistribution?: Record<string, number>;
  };
  /** Live Typesense match count for this company's currently-active listings; see JobDetailPageV2.tsx. */
  liveOpenRolesCount?: number | null;
  /** Employer Score grade for the brand rail pill; null when unscored or the profile isn't linkable. */
  employerScoreTier?: import('../../../../../utils/employer-score/mapScoreToTier').EmployerScoreTier | null;
  /** Median days-open for this role (from allSalaryStats.title), for the brand rail's context line. */
  typicalDaysOpen?: number | null;
  relatedPages: HubCrossLink[];
  effectiveSimilarJobs: unknown[];
  similarJobsSeeAllHref?: string;

  // Derived once in JobDetailPageV2.tsx, threaded through rather than re-computed here.
  isTyomarkkinatori: boolean;
  adjustedBrand: { panel: string; on: string; accentOnWhite: string };
  companyDisplayName: string;
  companyProfileHref: string;
  /** Still computed by JobDetailPageV2; no longer rendered under the H1 since the "Explore more" block at the bottom links the same hubs. */
  roleHubLinks?: Array<{ href: string; label: string }>;
  companyLinkTitle: string;
  insightsCueLabel: string;
  salaryStats: unknown;
  realSalaryLabel: string | null;
  displaySalaryLabel: string | null;
  /** The listing's pay in monthly EUR (the figures behind displaySalaryLabel), for the fit ledger's pay delta. */
  displaySalaryMonthly: { min: number; max: number } | null;
  companyAddressLine: string;
  hasRealCompanyAddress: boolean;
  finnishNetSalaryEstimate: FinnishNetSalaryEstimate | null;
  finnishNetSalaryExplanationText: string;
  taxIcebergHref?: string | null;
  originalTextExpanded: boolean;
  onToggleOriginalTextExpanded: () => void;
  mapModalOpen: boolean;
  onOpenMapModal: () => void;
  onCloseMapModal: () => void;
  miniHeaderVisible: boolean;
  /** Built in +Page.tsx; rendered here, below the card and above SimilarJobsSection, per design note. */
  alertCta?: React.ReactNode;
}

/**
 * TEC-512: "editorial rail" redesign of the job card (see JobDetailPageV2.tsx, which computes
 * every prop below once and renders this component; nothing here re-derives shared data). This
 * used to sit behind an A/B experiment and share JobDetailPageV2.tsx with a control layout;
 * both are gone now, this is just the page. Reuses BrandRail (with its enhancedDates visual
 * upgrade switched on), OverviewSectionHeader,
 * MetaGrid (for the secondary facts a single headline-facts line has no room for), CollectiveAgreementNotice,
 * and SimilarJobsSection/HubCrossLinks completely unchanged; only the headline facts row,
 * description block, and dossier (skills/education/benefits rail + salary/responsibilities/
 * requirements/recruitment-process/company main column) are new, per the design review.
 */
export function JobDetailPageV2EditorialRail(props: JobDetailPageV2EditorialRailProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const {
    displayJob,
    title,
    normalisedTitle,
    companyId,
    location,
    remoteness,
    remoteScopes,
    field,
    jobType,
    normalisedRoleLevel,
    requiredLanguages,
    peopleManagementTier,
    educationLevel,
    minimumYearsOfExperience,
    collectiveAgreementId,
    collectiveAgreementName,
    collectiveAgreementMatchType,
    collectiveAgreementReasoning,
    collectiveAgreementConfidence,
    jobLocale,
    displayDescription,
    overviewHeaderProps,
    applyUrl,
    isApplied,
    setAppliedJobState,
    onTailorCv,
    formattedPostedDate,
    postedDaysAgo,
    formattedLastSeenDate,
    formattedApplicationDate,
    formattedEstimatedExpiryDate,
    matchScoreData,
    matchBreakdownExpanded,
    onToggleMatchBreakdownExpanded,
    salaryRangeMin,
    salaryRangeMax,
    locationForChart,
    currency,
    salaryPeriod,
    salaryListingProvenance,
    perks,
    jobDetailsRef,
    currentLocaleTrends,
    liveOpenRolesCount,
    employerScoreTier,
    typicalDaysOpen,
    relatedPages,
    effectiveSimilarJobs,
    similarJobsSeeAllHref,
    isTyomarkkinatori,
    adjustedBrand,
    companyDisplayName,
    companyProfileHref,
    companyLinkTitle,
    insightsCueLabel,
    salaryStats,
    realSalaryLabel,
    displaySalaryLabel,
    displaySalaryMonthly,
    companyAddressLine,
    hasRealCompanyAddress,
    finnishNetSalaryEstimate,
    finnishNetSalaryExplanationText,
    taxIcebergHref,
    originalTextExpanded,
    onToggleOriginalTextExpanded,
    mapModalOpen,
    onOpenMapModal,
    onCloseMapModal,
    miniHeaderVisible,
    alertCta,
  } = props;

  // Destination for the home-commute chip: the geocoded company address when there is one,
  // otherwise the listing's own point (skipping the sea-parked placeholder used for unknowns).
  const commuteJobPoint = ((): { lat: number; lng: number } | null => {
    // companyGeocoordinates can exist without a printable street address, so unlike the map
    // modal this doesn't gate on hasRealCompanyAddress.
    const candidates = [
      displayJob.companyGeocoordinates,
      ...(Array.isArray(displayJob._geoloc) ? displayJob._geoloc : []),
    ];
    const point = candidates.find((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !isSeaFallbackGeoloc(p));
    return point ? { lat: point.lat, lng: point.lng } : null;
  })();

  // Travel times from the saved home address: fetched only while the map modal is open; the
  // Map chip then keeps the single preferred-mode figure (from this session's cache).
  const homeCommute = useHomeCommute(commuteJobPoint, mapModalOpen);
  const primaryHomeCommute = primaryCommute(homeCommute.durations, homeCommute.preferredMode);
  const mapChipNote = primaryHomeCommute
    ? formatMessage(
        { id: 'jobDetailPageV2.homeCommute.chip', defaultMessage: '{time} from home', description: 'Subline under the Map chip on the job page once a travel time from the saved home address is known; {time} is e.g. "24 min"' },
        { time: formatCommuteMinutes(formatMessage, primaryHomeCommute.minutes) },
      )
    : null;

  // With a printable street address the pin gets that label; with coordinates but no address
  // (common: geocoded from the city alone) the pin still shows, labelled with the location string,
  // so the Map chip, and with it the travel-time strip, isn't lost for those listings.
  const mapLocations = hasRealCompanyAddress
    ? [{ lat: displayJob.companyGeocoordinates!.lat, lng: displayJob.companyGeocoordinates!.lng, address: companyAddressLine, isPrimary: true }]
    : commuteJobPoint
      ? [{ lat: commuteJobPoint.lat, lng: commuteJobPoint.lng, address: location, isPrimary: true }]
      : [];

  return (
    <>
      <main id="job-main-content" className="mt-6">
        <div ref={jobDetailsRef} className="flex flex-col lg:flex-row items-stretch rounded-[24px] bg-white dark:bg-[#251A0D] shadow-[0_1px_3px_rgba(22,21,15,0.08)]">
          <BrandRail
            displayJob={displayJob}
            panelColor={adjustedBrand.panel}
            panelOn={adjustedBrand.on}
            accentOnWhite={adjustedBrand.accentOnWhite}
            applyUrl={applyUrl}
            isApplied={isApplied}
            setAppliedJobState={setAppliedJobState}
            onTailorCv={onTailorCv}
            realSalaryLabel={realSalaryLabel}
            formattedPostedDate={formattedPostedDate}
            postedDaysAgo={postedDaysAgo}
            employerScoreTier={employerScoreTier ?? null}
            typicalDaysOpen={typicalDaysOpen ?? null}
            formattedLastSeenDate={formattedLastSeenDate}
            formattedApplicationDate={formattedApplicationDate}
            formattedEstimatedExpiryDate={formattedEstimatedExpiryDate}
            companyId={companyId}
            companyName={companyDisplayName}
            isTyomarkkinatori={isTyomarkkinatori}
            location={location}
            companyProfileHref={companyProfileHref || undefined}
            companyLinkTitle={companyLinkTitle}
            insightsCueLabel={insightsCueLabel}
            locale={jobLocale}
            enhancedDates
            lastSeenRaw={displayJob.lastSeen ?? null}
            applicationDeadlineRaw={displayJob.applicationDeadline ?? null}
          />

          <div className="flex-1 min-w-0 flex flex-col gap-4 p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
              <div className="min-w-0">
                <h1 className="m-0 [font-family:'Space_Grotesk',sans-serif] text-[26px] md:text-[30px] font-bold leading-tight text-[#16150F] dark:text-[#F3EFE4]">
                  {fixMojibake(title || normalisedTitle || '')}
                </h1>
                {(normalisedTitle && normalisedTitle !== title) || (normalisedTitle && !isTyomarkkinatori) ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    {normalisedTitle && normalisedTitle !== title ? (
                      <span className="text-sm text-[#6E6659] dark:text-[#B0A896]">{fixMojibake(normalisedTitle)}</span>
                    ) : null}
                    {normalisedTitle && !isTyomarkkinatori ? (
                      <RoleFollowButton roleTitle={normalisedTitle} surface="job-page" />
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-none items-center">
                <OverviewSectionHeader
                  {...overviewHeaderProps}
                  ringColorOverride={adjustedBrand.panel}
                  labelColorOverride={{ light: '#16150F', dark: '#F3EFE4' }}
                  compact
                  onMatchDonutClick={
                    matchScoreData
                      ? () => {
                          if (!matchBreakdownExpanded) onToggleMatchBreakdownExpanded();
                          document.getElementById('match-breakdown-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      : undefined
                  }
                />
              </div>
            </div>

            <EditorialHeadlineFacts
              location={location}
              locationCount={mapLocations.length}
              onOpenMap={onOpenMapModal}
              remoteness={remoteness}
              remoteScopes={remoteScopes}
              jobType={jobType}
              normalisedRoleLevel={normalisedRoleLevel}
              displaySalaryLabel={displaySalaryLabel}
              brandColor={adjustedBrand.panel}
              numberOfPositions={displayJob.numberOfPositions}
              mapChipNote={mapChipNote}
            />

            <MetaGrid
              location={undefined}
              mapCommuteHref={buildJobMapCommuteHref(intl.locale as Locale, displayJob._geoloc)}
              remoteness={undefined}
              field={field}
              jobType={undefined}
              normalisedRoleLevel={undefined}
              requiredLanguages={requiredLanguages}
              peopleManagementTier={peopleManagementTier}
              educationLevel={educationLevel}
              minimumYearsOfExperience={minimumYearsOfExperience}
              accentColor={adjustedBrand.panel}
            />

            <CollectiveAgreementNotice
              collectiveAgreementId={collectiveAgreementId}
              collectiveAgreementName={collectiveAgreementName}
              collectiveAgreementMatchType={collectiveAgreementMatchType}
              collectiveAgreementReasoning={collectiveAgreementReasoning}
              collectiveAgreementConfidence={collectiveAgreementConfidence}
              jobLocale={jobLocale}
            />

            <EditorialDescription
              tldr={displayJob.tldr}
              displayDescription={displayDescription}
              expanded={originalTextExpanded}
              onToggleExpanded={onToggleOriginalTextExpanded}
              brandColor={adjustedBrand.panel}
            />

            <EditorialDossier
              displayJob={displayJob}
              perks={perks}
              salaryStats={salaryStats}
              locationSalaryStats={displayJob.locationSalaryStats}
              allSalaryStats={displayJob.allSalaryStats}
              jobSalaryMin={salaryRangeMin}
              jobSalaryMax={salaryRangeMax}
              currency={currency}
              salaryPeriod={salaryPeriod}
              jobLocale={jobLocale}
              jobLocation={locationForChart}
              jobTitle={normalisedTitle || title}
              jobRoleLevel={normalisedRoleLevel}
              jobSalaryListingProvenance={salaryListingProvenance}
              displaySalaryLabel={displaySalaryLabel}
              finnishNetSalaryEstimate={finnishNetSalaryEstimate}
              finnishNetSalaryExplanationText={finnishNetSalaryExplanationText}
              taxIcebergHref={taxIcebergHref}
              brandColor={adjustedBrand.panel}
              companyCardProps={{ displayJob, companyId, panelColor: adjustedBrand.panel, jobLocale, currentLocaleTrends, liveOpenRolesCount }}
            />

            {matchScoreData ? (
              <EditorialFitLedger
                displayJob={displayJob}
                matchScore={matchScoreData}
                matchScoreMeta={overviewHeaderProps.matchScoreMeta}
                jobSalaryMonthly={displaySalaryMonthly}
                brandColor={adjustedBrand.panel}
                brandAccentOnWhite={adjustedBrand.accentOnWhite}
                expanded={matchBreakdownExpanded}
                onToggleExpanded={onToggleMatchBreakdownExpanded}
                onTailorCv={onTailorCv}
              />
            ) : null}
          </div>
        </div>

        {alertCta ? <div className="mt-6">{alertCta}</div> : null}

        <div className="mt-6">
          <SimilarJobsSection similarJobs={effectiveSimilarJobs as never} seeAllHref={similarJobsSeeAllHref} brandColor={adjustedBrand.panel} />
        </div>
        <div className="mt-6">
          <HubCrossLinks
            id="related-pages"
            variant="cards"
            locale={jobLocale as 'en' | 'fi' | 'sv'}
            heading={formatMessage({ id: 'jobDetail.relatedPages.title', defaultMessage: 'Explore more', description: 'Heading of the job page block linking to the role, city, employer and salary hub pages' })}
            intro={formatMessage({ id: 'jobDetail.relatedPages.intro', defaultMessage: 'The role, city, employer and pay pages behind this listing.', description: 'One-line intro under the Explore more heading on the job page' })}
            links={relatedPages}
          />
        </div>
      </main>

      <JobLocationMapModal
        isOpen={mapModalOpen}
        onClose={onCloseMapModal}
        companyName={companyDisplayName}
        companyId={companyId ?? undefined}
        locations={mapLocations}
        commute={commuteJobPoint ? homeCommute : null}
        brandColor={adjustedBrand.panel}
      />

      {/* top-0: the site nav on this layout (MinimalChrome's <header>) isn't sticky, it scrolls
          away with the rest of the page, so there's nothing persistent above this to offset
          below — it belongs flush at the top of the viewport once visible. */}
      <div
        className={`lg:hidden fixed inset-x-0 top-0 z-20 flex items-center gap-2.5 px-4 shadow-[0_3px_10px_rgba(0,0,0,0.18)] transition-[transform,opacity] duration-300 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)] ${
          miniHeaderVisible ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0 pointer-events-none'
        }`}
        style={{ background: adjustedBrand.panel, color: adjustedBrand.on, height: 64 }}
        aria-hidden={!miniHeaderVisible}
      >
        <div className="h-8 w-8 flex-none rounded-lg bg-white p-1 box-border overflow-hidden flex items-center justify-center">
          <CompanyLogo
            companyName={displayJob.companyId || displayJob.companyName || ''}
            initialsSourceName={displayJob.companyName ?? displayJob.companyId}
            registryCompanyFormDescription={displayJob.registryCompanyFormDescription}
            isTyomarkkinatori={displayJob.isTyomarkkinatori}
            hostedLogoUrl={resolveSearchHitHostedLogoUrl(displayJob as unknown as Record<string, unknown>)}
            variant="bare"
            size={32}
            loading="eager"
          />
        </div>
        <div
          className="flex-1 min-w-0 [font-family:'Space_Grotesk',sans-serif] text-[14px] font-bold truncate"
          title={fixMojibake(companyDisplayName)}
        >
          {fixMojibake(companyDisplayName)}
        </div>
        <div className="flex-none">
          <ApplyButton
            applyUrl={applyUrl}
            isApplied={isApplied}
            displayJob={displayJob}
            applyBtnStyle={{ backgroundColor: '#FFFFFF', color: adjustedBrand.accentOnWhite }}
            applyBtnHoverStyle={{ filter: 'brightness(0.96)' }}
            effectiveBrandColour={adjustedBrand.panel}
            onSetApplied={setAppliedJobState}
            isSecondary
            compact
          />
        </div>
      </div>
    </>
  );
}
